import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  VStack,
  ScrollView,
  HStack,
  Switch,
  Text,
} from '@gluestack-ui/themed';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import GenerateWithAIBadge from '@/common/components/GenerateWithAIBadge';
import FormField from '@/common/components/forms/FormField';
import SettingsGroupCard from '@/common/components/forms/SettingsGroupCard';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import { useLecturerModuleData } from '@/common/hooks/lecturer/useLecturerModuleData';
import { useTenantGradingSettings } from '@/common/context/TenantSettingsStore';
import { useDialog } from '@/common/context/DialogContext';
import { useGenerateTaskProperty } from '@/common/hooks/lecturer/useGenerateTaskProperty';
import { isEmptyHtml } from '@/common/utils/htmlContent';
import log from '@/common/utils/logger';
import { TASK_EDITOR_TITLE_MAX_LENGTH } from '@/lecturer/ModuleEditor/TaskEditor/utils/taskEditorSchemas';
import { OralExamDescriptionPreview } from './OralExamDescriptionPreview';
import TaskObjectivesEditor from './TaskObjectivesEditor';
import EventConfigFields from './EventConfigFields';

interface TaskBaseFormProps {
  isReadOnly?: boolean;
  moduleId?: string;
  version?: string;
  taskId?: string | null;
}

const TaskBaseForm: React.FC<TaskBaseFormProps> = ({
  isReadOnly = true,
  moduleId,
  version,
  taskId: selectedTaskId,
}) => {
  const { getValues, setValue, watch } = useFormContext();
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const { editableDimensionKeys } = useTenantGradingSettings();
  const { isFeatureActive } = useUserFeatures();
  const taskAiEnabled = isFeatureActive('lecturer-task-ai');
  const [isGenerating, setIsGenerating] = useState(false);

  // Watch task type to set defaults for event config
  const taskType = watch('type');
  const descriptionValue = watch('description') ?? getValues('description') ?? '';
  const isOralExam = taskType === 'oral_exam';
  const taskFormId = watch('id');

  // Oral exam "additional comments" are optional and off by default. The RTE is
  // revealed only when the lecturer opts in, or when the loaded task already has
  // non-empty comments. Toggling off clears the field so it is genuinely empty.
  const [showComments, setShowComments] = useState(
    () => !isEmptyHtml(getValues('description') ?? ''),
  );
  useEffect(() => {
    setShowComments(!isEmptyHtml(getValues('description') ?? ''));
  }, [taskFormId, getValues]);

  const handleToggleComments = (enabled: boolean) => {
    setShowComments(enabled);
    if (!enabled) {
      setValue('description', '', { shouldValidate: true, shouldDirty: true });
    }
  };
  const { tasks } = useLecturerModuleData(moduleId, version, selectedTaskId);
  const oralExamConfig = useMemo(() => {
    const selectedTask = (Array.isArray(tasks) ? tasks : []).find(
      (tk: { id: string }) => tk.id === selectedTaskId,
    );
    return selectedTask?.oralExamConfig;
  }, [tasks, selectedTaskId]);
  const taskTypeChoices = [
    { label: 'Essay', value: 'essay' },
    { label: 'Activity', value: 'activity' },
    { label: 'Event', value: 'event' },
    { label: 'Multiple Choice', value: 'multiple_choice' },
    { label: 'Oral Exam', value: 'oral_exam' },
  ];

  const showsEventConfig = taskType === 'event' || taskType === 'oral_exam';

  // Set default values for event config when task type requires event delivery.
  useEffect(() => {
    if (showsEventConfig) {
      // Set default values for event config if they don't exist
      const currentUnlockType = getValues('eventConfig.unlockConditions.type');

      if (currentUnlockType === undefined || currentUnlockType === null) {
        setValue('eventConfig.unlockConditions.type', 'always', {
          shouldValidate: true,
          shouldDirty: false,
        });
      }
    }
  }, [showsEventConfig, getValues, setValue]);

  // Handle message from the stream
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMessage = (data: any) => {
    const message = data as { title?: string; description?: string };
    if (message?.title) {
      setValue('title', message.title, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    if (message?.description) {
      setValue('description', message.description, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  // Handle success
  const handleSuccess = (finalMessage: {
    valid: boolean;
    data: Record<string, unknown>;
  }) => {
    showToastMessage('success', 'Generated description successfully');
    setIsGenerating(false);

    const data = finalMessage.data as { objectives?: Array<{ dimension: string; objectives: string[] }> };

    // Handle objectives from final message only
    if (data?.objectives && Array.isArray(data.objectives)) {
      data.objectives.forEach(({ dimension, objectives }: { dimension: string; objectives: string[] }) => {
        if (dimension && Array.isArray(objectives)) {
          // Convert dimension to lowercase for matching
          const lowerDimension = dimension.toLowerCase();
          if (!editableDimensionKeys.includes(lowerDimension)) return;

          // Filter for valid objectives (non-empty strings)
          const validObjectives = objectives.filter((obj) => typeof obj === 'string' && obj.trim() !== '');

          // Update the form field for this dimension using setValue
          if (validObjectives.length > 0) {
            setValue(`analysis.${lowerDimension}`, validObjectives, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        }
      });
    }
  };

  // Handle error
  const handleError = (error: Error | string) => {
    log.info('[AI] Error generating description:', error);
    setIsGenerating(false);
  };

  const { startStream, isStreaming, stopStream } = useGenerateTaskProperty({
    property: 'description',
    moduleId: moduleId || '',
    version: version || '',
    taskId: selectedTaskId || '',
    onMessage: handleMessage,
    onServerClose: handleSuccess,
    onError: handleError,
  });

  // Clean up on unmount
  useEffect(() => (
    () => {
      stopStream();
      setIsGenerating(false);
    }
  ), [stopStream]);

  /**
   * Handle generating task description with AI
   */
  const handleGenerateDescription = () => {
    // Create draft task data
    const draftTaskData = {
      title: getValues('title') || '',
      description: getValues('description') || '',
    };

    // Set generating state synchronously
    setIsGenerating(true);

    // Clear the description field
    setValue('description', '', {
      shouldValidate: false,
      shouldDirty: false,
    });

    // Start the stream
    startStream(null, draftTaskData);
  };

  return (
    <Box flex={1} bg="$bgApp">
      <ScrollView flex={1} bg="$bgApp">
        <VStack space="xl" px="$6" py="$4">
          <SettingsGroupCard title="Task Details">
            <HStack space="lg" alignItems="flex-start">
              <Box flex={1}>
                <FormField
                  name="title"
                  type="text"
                  label="Title"
                  labelSize="xs"
                  fieldSize="sm"
                  errorTextSize="2xs"
                  readonly={isReadOnly || isStreaming}
                  rules={{
                    required: 'Task must have a title',
                    validate: (value: string) => {
                      if (value.length > TASK_EDITOR_TITLE_MAX_LENGTH) {
                        return `Title must be at most ${TASK_EDITOR_TITLE_MAX_LENGTH} characters`;
                      }
                      return true;
                    },
                  }}
                />
              </Box>

              <Box width={160}>
                <FormField
                  name="type"
                  type="select"
                  label="Type"
                  readonly={isReadOnly || isStreaming}
                  labelSize="xs"
                  fieldSize="sm"
                  errorTextSize="2xs"
                  defaultValue="essay"
                  choices={taskTypeChoices}
                />
              </Box>
            </HStack>

            {isOralExam ? (
              <VStack space="lg" mt="$2">
                <OralExamDescriptionPreview
                  resolvedQuestionCount={oralExamConfig?.resolvedQuestionCount}
                  resolvedSecondsPerQuestion={oralExamConfig?.resolvedSecondsPerQuestion}
                  poolSize={oralExamConfig?.questionPool?.length ?? 0}
                  commentsHtml={descriptionValue}
                />
                <VStack space="sm">
                  <HStack alignItems="center" space="sm">
                    <Switch
                      size="sm"
                      isDisabled={isReadOnly || isStreaming}
                      value={showComments}
                      onToggle={handleToggleComments}
                      testID="oral-exam-additional-comments-toggle"
                    />
                    <Text size="xs" color="$textBody">
                      {t('lecturer.moduleEditor.taskEditor.description.addAdditionalComments')}
                    </Text>
                  </HStack>
                  {showComments && (
                    <FormField
                      key={watch('id')}
                      name="description"
                      type="rte"
                      readonly={isReadOnly || isStreaming}
                      placeholder={t('lecturer.moduleEditor.taskEditor.description.additionalCommentsPlaceholder')}
                      labelSize="xs"
                      fieldSize="xs"
                      fieldMinHeight={80}
                      fieldMaxHeight={200}
                      rules={{}}
                      errorTextSize="2xs"
                    />
                  )}
                </VStack>
              </VStack>
            ) : (
              <Box>
                <FormField
                  key={watch('id')}
                  name="description"
                  type="rte"
                  readonly={isReadOnly || isStreaming}
                  borderless
                  labelSize="xs"
                  fieldSize="xs"
                  fieldHeight={watch('type') === 'event' ? 150 : 300}
                  errorTextSize="2xs"
                  rules={{
                    required: isStreaming || isGenerating ? false : 'Task must have a description',
                    validate: (value: string) => {
                      if (value.length < 20 && !isStreaming && !isGenerating) {
                        return 'Description must be longer than 20 characters';
                      }
                      return true;
                    },
                  }}
                />
                {taskAiEnabled && !isReadOnly && (
                  <HStack justifyContent="flex-end" mt="$1">
                    <GenerateWithAIBadge
                      onGenerate={handleGenerateDescription}
                      isStreaming={isStreaming}
                      enabled={!!selectedTaskId}
                      label={t('generateWithAI.descriptionLabel')}
                      testID="description-generate-badge"
                    />
                  </HStack>
                )}
              </Box>
            )}
          </SettingsGroupCard>

          {/* Event-backed delivery configuration */}
          {showsEventConfig && (
            <SettingsGroupCard title="Event Configuration">
              <EventConfigFields
                isReadOnly={isReadOnly || isStreaming}
                moduleId={moduleId}
                version={version}
                taskId={selectedTaskId}
              />
            </SettingsGroupCard>
          )}

          {/* Task Objectives - component includes its own header */}
          {watch('type') === 'essay' && (
            <TaskObjectivesEditor
              isReadOnly={isReadOnly}
              moduleId={moduleId}
              version={version}
              taskId={selectedTaskId}
            />
          )}
        </VStack>
      </ScrollView>
    </Box>
  );
};

export default TaskBaseForm;
